import { env } from '@/lib/env'
import { coerceDigestSetting, type DigestSetting } from '../../workers/digest-format'

/**
 * THE WEEKLY-REPORT CLIENT (Build 9, commit G).
 *
 * Talks to the owner-only /api/admin/digest routes (workers/api.ts). The shape
 * and every word the card shows come from workers/digest-format.ts — the
 * dependency-free module the engine uses too — so this file carries no wording
 * of its own. React-free, like src/lib/renewals.ts.
 *
 * NEVER TRUST THE WIRE. The response is coerced by coerceDigestSetting, which
 * reads anything missing or garbled as the state that promises least.
 */

export type { DigestSetting }

export class DigestError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message)
    this.name = 'DigestError'
  }
}

async function request(
  token: string | null,
  init: { method: 'GET' | 'POST'; body?: unknown },
): Promise<unknown> {
  if (!token) throw new DigestError('Your sign-in has expired. Please sign in again.', 401)
  let res: Response
  try {
    res = await fetch(`${env.VITE_API_URL}/api/admin/digest`, {
      method: init.method,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(init.body === undefined ? {} : { 'Content-Type': 'application/json' }),
      },
      body: init.body === undefined ? undefined : JSON.stringify(init.body),
    })
  } catch {
    throw new DigestError('No connection. Check your network and try again.')
  }
  let data: unknown
  try {
    data = await res.json()
  } catch {
    throw new DigestError('The engine returned an unreadable response.', res.status)
  }
  if (!res.ok) {
    const message = (data as { message?: unknown } | null)?.message
    throw new DigestError(typeof message === 'string' ? message : 'Something went wrong. Please try again.', res.status)
  }
  return data
}

/** His weekly-report setting, as the card shows it. */
export async function fetchDigestSetting(token: string | null): Promise<DigestSetting> {
  return coerceDigestSetting(await request(token, { method: 'GET' }))
}

/**
 * Switch it on or off. Answers the state the engine recorded — the card draws
 * what the server says is now true, never what the button hoped for.
 */
export async function setDigestSetting(token: string | null, on: boolean): Promise<DigestSetting> {
  return coerceDigestSetting(await request(token, { method: 'POST', body: { on } }))
}
